/// <amd-module name="Wit/WorkItemForm/Attachments/AttachmentsGrid" />
import * as React from "react";
import { useMemo } from "react";
import { IObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Ago } from "VSSUI/Ago";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ColumnSorting, ITableColumn, renderSimpleCell, renderSimpleCellValue, SimpleTableCell, sortItems, SortOrder, Table } from "VSSUI/Table";
import { ILinkInfo } from "Wit/WorkItemDataManager/Contracts";
import { getDateInUserTimeZone,  tooltipTimeFormat } from "Wit/Common/DateUtils";
import * as Resources from "Wit/WorkItemForm/Resources";
import { curryAttachmentActions, IAttachmentActions } from "./AttachmentActions";
import { AttachmentMoreButton } from "./AttachmentMoreButton";
import { formatFileSize, getAttachmentIconName } from "./AttachmentUtils";

export interface IAttachmentsGridProps extends IAttachmentActions {
    attachments: IObservableArray<ILinkInfo>;
}

export function AttachmentsGrid({
    attachments,
    onEditComment,
    onDownloadAttachment,
    onDeleteAttachment,
    onPreviewAttachment,
    onCopyAttachmentUrl
}: IAttachmentsGridProps) {
    const setAttachments = (newValue: ILinkInfo[]) => attachments.value = newValue;

    const attachmentsGridColumns = useMemo(() => getColumns({ onEditComment, onDownloadAttachment, onDeleteAttachment, onPreviewAttachment, onCopyAttachmentUrl }), [
        onEditComment,
        onDownloadAttachment,
        onDeleteAttachment,
        onPreviewAttachment,
        onCopyAttachmentUrl
    ]);

    const sortingBehavior = useMemo(() => getSortingBehavior(attachments, setAttachments, attachmentsGridColumns), [attachmentsGridColumns]);

    return <Table className="work-item-attachments-grid" behaviors={[sortingBehavior]} columns={attachmentsGridColumns} itemProvider={attachments} />;
}

function getColumns(actions: IAttachmentActions): ITableColumn<ILinkInfo>[] {
    return [
        {
            id: "OriginalName",
            name: Resources.AttachmentNameColumn,
            readonly: true,
            sortProps: {},
            renderCell: (iRow, iCol, col, item) => {
                const curriedActions = curryAttachmentActions(item, actions);
                const fileName = item.OriginalName ?? "";
                return (
                    <SimpleTableCell ariaRowIndex={iRow} columnIndex={iCol} key={iCol} tableColumn={col}>
                        <div className="attachment-name-cell flex-row flex-center justify-space-between">
                            <div className="flex-row text-ellipsis">
                                {item.FilePath ? (
                                    <Icon iconName={getAttachmentIconName(fileName)} className="icon-margin" ariaHidden />
                                ) : (
                                    <Spinner className="icon-margin" size={SpinnerSize.small} />
                                )}
                                <Link className="text-ellipsis" onClick={() => curriedActions.onPreviewAttachment?.()}>{fileName}</Link>
                            </div>
                            <AttachmentMoreButton className="attachment-more-button" {...curriedActions} />
                        </div>
                    </SimpleTableCell>
                );
            },
            width: new ObservableValue(-3 / 8),
            onSize: onSize
        },
        {
            id: "Length",
            name: Resources.AttachmentSizeColumn,
            readonly: true,
            sortProps: {},
            renderCell: (iRow, iCol, col, item) => renderSimpleCellValue(iCol, col, formatFileSize(item.Length), iRow),
            width: new ObservableValue(-1 / 8),
            onSize: onSize
        },
        {
            id: "AddedDate",
            name: Resources.AttachmentDateAddedColumn,
            readonly: true,
            sortProps: {},
            renderCell: (iRow, iCol, col, item) => (
                <SimpleTableCell ariaRowIndex={iRow} columnIndex={iCol} key={iCol} tableColumn={col}>
                    {item.AddedDate && <Ago date={getDateInUserTimeZone(item.AddedDate)} currentDate={getDateInUserTimeZone()} tooltipTimeFormat={tooltipTimeFormat} />}
                </SimpleTableCell>
            ),
            width: new ObservableValue(-1 / 8),
            onSize: onSize
        },
        {
            id: "Comment",
            name: Resources.AttachmentCommentsColumn,
            readonly: true,
            sortProps: {},
            renderCell: renderSimpleCell,
            width: new ObservableValue(-3 / 8),
            onSize: onSize
        }
    ];
}


function onSize(event: MouseEvent, index: number, width: number, column: ITableColumn<ILinkInfo>) {
    (column.width as ObservableValue<number>).value = width;
}

function getSortingBehavior(items: IObservableArray<ILinkInfo>, setItems: (items: ILinkInfo[]) => void, columns: ITableColumn<ILinkInfo>[]) {
    // Create the sorting behavior (delegate that is called when a column is sorted).
    return new ColumnSorting<ILinkInfo>(
        (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
            // This will sort items.value in place
            sortItems<ILinkInfo>(columnIndex, proposedSortOrder, sortFunctions, columns, items.value);
            // This will update the observable so that changes to the grid are visible
            setItems(items.value);
        }
    );
}

const sortFunctions = [
    // Sort on Name column
    (item1: ILinkInfo, item2: ILinkInfo): number => compareNullableStrings(item1.OriginalName, item2.OriginalName),

    // Sort on Length column
    (item1: ILinkInfo, item2: ILinkInfo): number => {
        return (item1.Length ?? 0) - (item2.Length ?? 0);
    },

    // Sort on AddedDate column
    (item1: ILinkInfo, item2: ILinkInfo): number => {
        return (item1.AddedDate?.getTime() ?? 0) - (item2.AddedDate?.getTime() ?? 0);
    },

    // Sort on Comment column
    (item1: ILinkInfo, item2: ILinkInfo): number => compareNullableStrings(item1.Comment, item2.Comment)
];

function compareNullableStrings(a: string | null | undefined, b: string | null | undefined): number {
    if (!a && !b) {
        return 0;
    }
    if (!a) {
        return 1;
    }
    if (!b) {
        return -1;
    }
    return a.localeCompare(b, navigator.language);
}
